import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { LOGO_URL } from "@/lib/api";

const SESSION_KEY = "ep_intro_shown";

/**
 * LogoIntro — cinematic full-screen logo reveal that plays once per session.
 *
 * Animation timeline (default 3.5s total):
 *   Phase A (0 – 35%): black overlay + logo fades in + soft glow + scale
 *   Phase B (35 – 70%): logo holds, gold shimmer sweeps across
 *   Phase C (70 – 95%): logo shrinks + slides toward top-left header area
 *   Phase D (95 – 100%): overlay fades out, content interactive
 *
 * Skippable (click anywhere or press Esc). Respects prefers-reduced-motion.
 */
export default function LogoIntro({ settings }) {
  const location = useLocation();
  const [phase, setPhase] = useState(0); // 0 = idle, 1 = playing, 2 = travel, 3 = done
  const [forceShow, setForceShow] = useState(false); // admin preview
  const timersRef = useRef([]);

  const homepageOnly = settings?.intro_homepage_only !== false; // default true
  const isHomepage = location.pathname === "/";
  const enabled = settings && settings.intro_enabled !== false;
  const duration = Math.max(1500, Math.min(8000, settings?.intro_duration_ms ?? 3500));

  // Listen for admin "preview" requests (event + URL query)
  useEffect(() => {
    const onPreview = () => { sessionStorage.removeItem(SESSION_KEY); setForceShow(true); setPhase(1); };
    window.addEventListener("ep:preview-intro", onPreview);
    // Also: ?preview_intro=1 query param triggers it once
    const params = new URLSearchParams(window.location.search);
    if (params.get("preview_intro") === "1") {
      sessionStorage.removeItem(SESSION_KEY);
      setForceShow(true);
      setPhase(1);
    }
    return () => window.removeEventListener("ep:preview-intro", onPreview);
  }, []);

  // Reduced-motion: skip the animation entirely
  const reducedMotion = typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (!enabled) return;
    if (homepageOnly && !isHomepage && !forceShow) return;
    if (sessionStorage.getItem(SESSION_KEY) && !forceShow) return;
    if (reducedMotion && !forceShow) { sessionStorage.setItem(SESSION_KEY, "1"); return; }

    setPhase(1);
    const holdMs = Math.floor(duration * 0.70);
    const travelMs = Math.floor(duration * 0.25);
    const t1 = setTimeout(() => setPhase(2), holdMs);
    const t2 = setTimeout(() => { setPhase(3); sessionStorage.setItem(SESSION_KEY, "1"); setForceShow(false); },
                          holdMs + travelMs);
    timersRef.current = [t1, t2];
    return () => timersRef.current.forEach(clearTimeout);
  }, [enabled, homepageOnly, isHomepage, forceShow, duration, reducedMotion]);

  // Skip on click / Escape
  useEffect(() => {
    if (phase === 0 || phase === 3) return;
    const skip = () => { setPhase(3); sessionStorage.setItem(SESSION_KEY, "1"); setForceShow(false); };
    const onKey = (e) => { if (e.key === "Escape") skip(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase]);

  if (phase === 0 || phase === 3) return null;

  const logo = settings?.intro_logo_url || LOGO_URL;
  const tagline = settings?.intro_tagline || "Where Every Love Story Becomes Cinema";
  const traveling = phase === 2;

  return (
    <div
      data-testid="logo-intro"
      onClick={() => { setPhase(3); sessionStorage.setItem(SESSION_KEY, "1"); setForceShow(false); }}
      className="fixed inset-0 z-[300] flex items-center justify-center cursor-pointer"
      style={{
        background: "radial-gradient(ellipse at center, #0a0a0a 0%, #000000 70%)",
        animation: traveling ? "ep-intro-overlay-out 900ms ease-in-out forwards" : "ep-intro-overlay-in 600ms ease-out",
      }}
      role="dialog" aria-label="Site introduction"
    >
      {/* Grain overlay for cinematic texture */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
      />

      {/* Top + bottom gold accent lines (cinematic letterbox feel) */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent"
           style={{ animation: "ep-intro-line 1500ms ease-out both" }} />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent"
           style={{ animation: "ep-intro-line 1500ms 200ms ease-out both" }} />

      <div
        data-testid="logo-intro-content"
        className="relative flex flex-col items-center"
        style={{
          animation: traveling
            ? "ep-intro-travel 900ms cubic-bezier(0.65, 0, 0.35, 1) forwards"
            : "ep-intro-zoom 1800ms cubic-bezier(0.22, 0.61, 0.36, 1) both",
        }}
      >
        {/* Soft golden glow behind logo */}
        <div className="absolute inset-0 -m-20 rounded-full pointer-events-none"
             style={{
               background: "radial-gradient(circle at center, rgba(212,175,55,0.18), transparent 70%)",
               filter: "blur(40px)",
               animation: "ep-intro-glow 2000ms ease-out both",
             }} />

        {/* Shimmer sweep */}
        <div className="relative overflow-hidden">
          <img src={logo} alt="Elite Pixels" className="h-32 md:h-44 object-contain relative z-10 drop-shadow-2xl" />
          <div className="absolute inset-0 z-20 pointer-events-none"
               style={{
                 background: "linear-gradient(110deg, transparent 25%, rgba(212,175,55,0.55) 50%, transparent 75%)",
                 transform: "translateX(-100%)",
                 animation: "ep-intro-shimmer 1800ms 800ms ease-in-out 1 forwards",
               }} />
        </div>

        {/* Tagline */}
        <div className="mt-7 text-center" style={{ animation: "ep-intro-fade 1200ms 900ms ease-out both" }}>
          <div className="flex items-center justify-center gap-4 mb-3">
            <span className="h-px w-12 bg-[#D4AF37]/60" />
            <span className="text-[0.55rem] tracking-[0.55em] uppercase text-[#D4AF37]">Elite Pixels</span>
            <span className="h-px w-12 bg-[#D4AF37]/60" />
          </div>
          <p className="font-serif text-base md:text-lg italic font-light text-white/80 max-w-sm mx-auto">{tagline}</p>
        </div>
      </div>

      {/* Skip hint */}
      <button
        data-testid="logo-intro-skip"
        onClick={(e) => { e.stopPropagation(); setPhase(3); sessionStorage.setItem(SESSION_KEY, "1"); setForceShow(false); }}
        className="absolute bottom-6 right-6 text-[0.55rem] tracking-[0.3em] uppercase text-white/30 hover:text-white/70 transition"
        style={{ animation: "ep-intro-fade 1200ms 1800ms ease-out both" }}
      >
        Skip ›
      </button>

      <style>{`
        @keyframes ep-intro-overlay-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes ep-intro-overlay-out {
          0% { opacity: 1; }
          100% { opacity: 0; pointer-events: none; }
        }
        @keyframes ep-intro-zoom {
          0% { opacity: 0; transform: scale(0.85); filter: blur(8px); }
          40% { opacity: 1; transform: scale(1); filter: blur(0); }
          100% { opacity: 1; transform: scale(1.04); filter: blur(0); }
        }
        @keyframes ep-intro-travel {
          0% { opacity: 1; transform: scale(1.04) translate(0, 0); }
          100% {
            opacity: 0.05;
            transform: scale(0.22) translate(-180vw, -42vh);
          }
        }
        @keyframes ep-intro-glow {
          0% { opacity: 0; }
          60% { opacity: 1; }
          100% { opacity: 0.7; }
        }
        @keyframes ep-intro-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(120%); }
        }
        @keyframes ep-intro-line {
          0% { opacity: 0; transform: scaleX(0); }
          100% { opacity: 1; transform: scaleX(1); }
        }
        @keyframes ep-intro-fade {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
